import React, { forwardRef, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import 'react-quill/dist/quill.snow.css';

export interface RichTextEditorProps {
  getValue: (value: string) => void;
  className?: string;
  label?: string;
  value?: string;
  labelClassName?: string;
  required?: boolean;
}
const ReactQuill = dynamic(() => import('react-quill'), { ssr: false });
const RichTextEditor = forwardRef<HTMLInputElement, RichTextEditorProps>(function RichTextEditor(props, ref) {
  const { getValue, className, label, labelClassName, required } = props;
  const [value, setValue] = props.value ? useState(props.value) : useState('');
  const maxLength = 512;
  const [remainingChars, setRemainingChars] = useState(maxLength); // Set maximum characters
  useEffect(() => {
    setValue(props.value);
  }, [props.value]);
  const handleChange = (content) => {
    const text = content.replace(/<[^>]*>/g, '');
    if (text.length <= maxLength) {
      setValue(content);
      setRemainingChars(maxLength - text.length);
      getValue(content);
    } else {
      const trimmedContent = text.slice(0, maxLength);
      setValue(trimmedContent);
      getValue(trimmedContent);
      setRemainingChars(0);
    }
  };
  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['clean'], // Remove formatting
    ],
  };
  const formats = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet'];
  return (
    <div className={className}>
      <div className="flex">
        <label className={labelClassName}>{label}</label>
        {required && <span style={{ color: 'red' }}>*</span>}
        {!required && <span className="text-gray-900 font-normal text-xs ml-1 pt-1">(optional)</span>}
      </div>
      {remainingChars <= 0 && <p>Maximum character limit of {maxLength} exceeded</p>}
      <ReactQuill
        onKeyDown={(e) => {
          if (remainingChars <= 0 && e.key !== 'Backspace') {
            e.preventDefault();
          }
        }}
        theme="snow"
        value={value}
        onChange={handleChange}
        placeholder={`(Max ${maxLength} characters)`}
        modules={modules}
        formats={formats}
      />
    </div>
  );
});

export { RichTextEditor };
